import React from "react";
import { ArrowLeft } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="font-serif text-2xl sm:text-3xl text-[#1F2E4D] leading-tight">
      {title}
    </h2>
    <div className="text-[15px] text-[#4A5568] leading-relaxed mt-3 space-y-3">
      {children}
    </div>
  </section>
);

const Row = ({ name, purpose, lifetime }) => (
  <tr className="border-b border-[#E8E2D4] last:border-b-0 align-top">
    <td className="py-3 pr-4 font-medium text-[#1F2E4D]">{name}</td>
    <td className="py-3 pr-4">{purpose}</td>
    <td className="py-3">{lifetime}</td>
  </tr>
);

export const CookiesPolicy = ({ onBack }) => {
  const lastUpdated = "December 2025";
  return (
    <main data-testid="cookies-view" className="hr-paper-bg">
      <section className="max-w-2xl mx-auto px-6 sm:px-8 pt-6 pb-24">
        <button
          type="button"
          onClick={onBack}
          data-testid="cookies-back-btn"
          className="inline-flex items-center gap-2 text-sm text-[#1F2E4D] hover:text-[#0E7C7B] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden />
          Back
        </button>

        <header className="mt-8">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-[#1F2E4D]">
            Legal
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight text-[#0E7C7B] mt-3">
            Cookies Policy.
          </h1>
          <p className="text-sm text-[#4A5568] mt-3">
            Last updated: {lastUpdated}
          </p>
          <p className="text-base text-[#4A5568] mt-5 leading-relaxed">
            This Cookies Policy explains how Heart Reach Counselling Services
            uses cookies and similar technologies on this website, in line with
            the UK Privacy and Electronic Communications Regulations (PECR), UK
            GDPR, and Zimbabwe's Cyber and Data Protection Act (2021).
          </p>
        </header>

        <Section title="1. What are cookies?">
          <p>
            Cookies are small text files stored on your device when you visit a
            website. We also use similar technologies such as the browser's
            local storage and service-worker caches to make the site work
            properly offline.
          </p>
        </Section>

        <Section title="2. The cookies and storage we use">
          <p>
            We aim to keep things minimal. We do not use third-party advertising
            or behavioural-tracking cookies.
          </p>
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.18em] text-[#0E7C7B] border-b border-[#E8E2D4]">
                  <th className="py-2 pr-4 font-semibold">Name</th>
                  <th className="py-2 pr-4 font-semibold">Purpose</th>
                  <th className="py-2 font-semibold">Lifetime</th>
                </tr>
              </thead>
              <tbody className="text-[#4A5568]">
                <Row
                  name="hr_intake_queue_v1"
                  purpose="Local storage of intake submissions made while offline, so they can sync once you reconnect."
                  lifetime="Until sent or manually cleared"
                />
                <Row
                  name="hr_sw_purged"
                  purpose="Session storage flag used to safely refresh stale offline caches."
                  lifetime="Session"
                />
                <Row
                  name="Service-worker cache"
                  purpose="Caches HTML, CSS, JS and images so the site loads instantly and works offline."
                  lifetime="Until you clear site data"
                />
                <Row
                  name="Analytics cookies (anonymised)"
                  purpose="If enabled, used only to measure aggregate site usage. We do not use these for advertising."
                  lifetime="Up to 12 months"
                />
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="3. Your choices">
          <Bullets />
        </Section>

        <Section title="4. Legal basis">
          <p>
            Strictly necessary cookies and storage (those required to deliver
            the service you have requested) are used on the basis of our
            legitimate interests and the PECR strictly-necessary exemption.
            Any non-essential cookies are only used with your consent, which
            you can withdraw at any time.
          </p>
        </Section>

        <Section title="5. Updates">
          <p>
            We may update this policy from time to time. The "Last updated"
            date above shows the latest revision.
          </p>
        </Section>

        <Section title="6. Contact">
          <p>
            For any cookies or privacy questions please email{" "}
            <a
              href="mailto:admin@heartreachcounselling.com"
              className="text-[#0E7C7B] underline underline-offset-4"
            >
              admin@heartreachcounselling.com
            </a>
            .
          </p>
        </Section>
      </section>
    </main>
  );
};

const Bullets = () => (
  <ul className="list-disc pl-6 space-y-2">
    <li>
      You can control or delete cookies through your browser settings — see
      your browser's help pages for details.
    </li>
    <li>
      You can clear the site's offline cache by uninstalling the app
      ("Heart Reach") from your home screen, or via your browser's "Clear
      site data" option.
    </li>
    <li>
      Blocking essential cookies and storage may stop parts of the site
      working — for example, the offline form-save feature.
    </li>
  </ul>
);

export default CookiesPolicy;
